import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { motion, LayoutGroup, AnimatePresence } from "framer-motion";
import { Check, ChevronDown, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  computeEqualSplits,
  computeExactSplits,
  computePercentSplits,
  computeSharesSplits,
  payerFieldsFor,
  resolveExpensePayer,
  splitParticipants,
  toTripCurrency,
  formatMoney,
  rate,
  splitsBalance,
  SUPPORTED_CURRENCIES,
  Timestamp,
  uuid,
  type ExpenseEntry,
  type ExpenseType,
  type SplitMode,
  type Trip,
} from "@travelix/core";

import { useTrip } from "@/hooks/use-trip";
import { useTripMutation } from "@/hooks/use-trip-mutation";
import { useAuth } from "@/lib/auth-provider";
import { isExpenseLoggingClosed } from "@/lib/trip-readonly";
import { logAnalyticsEvent } from "@/lib/analytics";
import { getTripDateBounds } from "@/lib/trip-date-bounds";
import { parseAmount, currencySymbol, todayLocalYmd, parseYmdLocalNoon } from "@/lib/money-input";
import { Sheet, SheetContent, SheetHeader, SheetBody, SheetFooter } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/ui/field";
import { Avatar } from "@/components/ui/avatar";
import { Segmented } from "@/components/ui/segmented";
import { Skeleton } from "@/components/ui/skeleton";
import { ConfirmDialog } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import {
  addExpenseToTrip,
  removeExpenseFromTrip,
  updateExpenseInTrip,
  unwrap,
  queryKeys,
  displayNameOf,
} from "@/lib/api";

const CATEGORIES: { key: ExpenseType; label: string; color: string }[] = [
  { key: "restaurants", label: "Restaurants", color: "#E4572E" },
  { key: "drinks",      label: "Drinks",      color: "#B87E28" },
  { key: "hotels",      label: "Hotels",      color: "#C97B3F" },
  { key: "flights",     label: "Flights",     color: "#4A7C9E" },
  { key: "transportation", label: "Transport", color: "#5E8A6E" },
  { key: "activities",  label: "Activities",  color: "#D08A3F" },
  { key: "tickets",     label: "Tickets",     color: "#D9A85A" },
  { key: "shopping",    label: "Shopping",    color: "#A16CB0" },
  { key: "groceries",   label: "Groceries",   color: "#5E8A6E" },
  { key: "misc",        label: "Misc",        color: "#8B837C" },
];

export default function AddExpenseRoute() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data: trip, isLoading } = useTrip(id);
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("editId");

  // Existing expense — present when the route is opened with ?editId=X.
  // The whole form hydrates from this once the trip loads.
  const existing = useMemo<ExpenseEntry | null>(
    () =>
      editId && trip
        ? (trip.expenses ?? []).find((e) => e.id === editId) ?? null
        : null,
    [editId, trip],
  );
  const isEdit = !!editId;

  // Expenses stay open for a week past the trip's end date, then lock.
  // Bounce direct URL access with a soft toast. Toast fires from an
  // effect; the actual redirect happens below all hooks (see the
  // Navigate return before the JSX) so we never call hooks conditionally
  // — react-hooks/rules-of-hooks would (rightly) fail CI.
  const readOnly = isExpenseLoggingClosed(trip);
  useEffect(() => {
    if (readOnly && id) {
      toast.info("The window to add or edit expenses for this trip has closed.");
    }
  }, [readOnly, id]);

  const close = () => navigate(`/trip/${id}/expenses`, { replace: true });

  // ─── Form state ─── (small enough that plain useState is clearer than RHF)
  const [amount, setAmount] = useState("");
  const [name, setName] = useState("");
  const [type, setType] = useState<ExpenseType>("restaurants");
  const [error, setError] = useState<string | null>(null);

  // Payer — defaults to the current user. Toggling this row expands the
  // member list inline; picking one collapses it. Only one is open at a
  // time.
  const [payerUid, setPayerUid] = useState<string | null>(null);
  const [payerPickerOpen, setPayerPickerOpen] = useState(false);

  // Expense date — defaults to "today", clamped to trip bounds. Stored
  // as yyyy-MM-dd so we control timezone rather than letting the
  // datetime-local input do it. Empty string means "use now".
  const [expenseYmd, setExpenseYmd] = useState<string>(() => todayLocalYmd());

  // Currency the merchant charged in. When it differs from trip
  // currency, we run FX and persist both the trip-currency amount and
  // the original alongside the rate. Null = same as trip currency.
  const [chargeCurrency, setChargeCurrency] = useState<string | null>(null);

  // Split mode + per-member weights for non-equal modes.
  const [splitMode, setSplitMode] = useState<SplitMode>("equal");
  // Keyed by userId. Values are strings so we can render partial
  // inputs ("33." while the user is typing).
  const [percentages, setPercentages] = useState<Record<string, string>>({});
  const [shares, setShares] = useState<Record<string, string>>({});
  const [exacts, setExacts] = useState<Record<string, string>>({});

  // Confirm dialog for the edit-mode delete action.
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  // Hydrate every field from the existing entry the first time it
  // appears (edit mode). Guarded by a ref-shaped flag so subsequent
  // re-renders don't clobber the user's edits.
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    if (!existing || hydrated) return;
    setAmount(String(existing.originalAmount ?? existing.amount));
    setName(existing.name);
    setType(existing.type);
    setPayerUid(existing.payerUid ?? null);
    setChargeCurrency(
      existing.originalCurrency && existing.originalCurrency !== (existing.currency ?? "")
        ? existing.originalCurrency
        : null,
    );
    // Firestore Timestamp → yyyy-MM-dd (viewer's local time).
    try {
      const d = existing.expenseDate.toDate();
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      setExpenseYmd(`${y}-${m}-${day}`);
    } catch {
      // Keep the default (today) if the timestamp can't be converted.
    }
    const mode = existing.splitMode ?? "equal";
    setSplitMode(mode);
    if (mode === "percent") {
      const p: Record<string, string> = {};
      for (const s of existing.splits) p[s.userId] = String(s.percentage ?? 0);
      setPercentages(p);
    } else if (mode === "shares") {
      // Raw shares aren't stored — prefill using split amounts, which
      // are proportional to shares. The user can renormalise.
      const sh: Record<string, string> = {};
      for (const s of existing.splits) sh[s.userId] = String(s.amount);
      setShares(sh);
    } else if (mode === "exact") {
      const ex: Record<string, string> = {};
      for (const s of existing.splits) ex[s.userId] = String(s.amount);
      setExacts(ex);
    }
    setHydrated(true);
  }, [existing, hydrated]);

  const currency = trip?.currency ?? "EUR";
  const members = useMemo(
    () =>
      trip
        ? Object.entries(trip.members).map(([uid, m]) => ({ userId: uid, userName: m.name }))
        : [],
    [trip],
  );

  const bounds = useMemo(
    () =>
      trip
        ? getTripDateBounds(trip)
        : {
            minAttr: "",
            maxAttr: "",
            minMs: -Infinity,
            maxMs: Infinity,
            startLabel: "",
            endLabel: "",
          },
    [trip],
  );
  // datetime-local uses "yyyy-MM-ddTHH:mm"; date input uses just yyyy-MM-dd.
  const dateMin = bounds.minAttr ? bounds.minAttr.slice(0, 10) : undefined;
  const dateMax = bounds.maxAttr ? bounds.maxAttr.slice(0, 10) : undefined;

  const rawAmount = parseAmount(amount);
  const activeCurrency = chargeCurrency ?? currency;
  // Convert the merchant-charge amount into trip currency for balance
  // math. When currencies match, this is an identity.
  // On edit with the currency unchanged, reuse the stored rate so the
  // saved amount doesn't drift with today's rate. A missing rate yields
  // null and blocks the save — never fall back to the raw amount.
  const lockedRate =
    existing?.fxRate != null && existing.originalCurrency === activeCurrency
      ? existing.fxRate
      : null;
  const convertedAmount = useMemo(() => {
    if (rawAmount <= 0) return 0;
    return toTripCurrency(rawAmount, activeCurrency, currency, lockedRate);
  }, [rawAmount, activeCurrency, currency, lockedRate]);
  const fxUnavailable = convertedAmount === null;
  const fxRateValue = useMemo(
    () =>
      activeCurrency === currency ? null : (lockedRate ?? rate(activeCurrency, currency)),
    [activeCurrency, currency, lockedRate],
  );
  // Backwards-compatible alias — the mutation path already reads amountNum.
  const amountNum = convertedAmount ?? 0;

  // Edits keep the original participants; new expenses use all members.
  const participants = useMemo(
    () => splitParticipants(existing, members, splitMode),
    [existing, members, splitMode],
  );

  // Split computation switches on mode. Non-equal modes throw on invalid
  // input; we suppress those inside a memo and treat as "no valid split
  // yet" so the Save button disables via splitsValid.
  const splits = useMemo(() => {
    if (amountNum <= 0 || participants.length === 0) return [];
    try {
      if (splitMode === "equal") return computeEqualSplits(amountNum, participants, currency);
      if (splitMode === "percent") {
        const pcts = participants.map((m) => parseFloat(percentages[m.userId] ?? "0") || 0);
        return computePercentSplits(amountNum, participants, pcts, currency);
      }
      if (splitMode === "shares") {
        const sh = participants.map((m) => parseFloat(shares[m.userId] ?? "0") || 0);
        return computeSharesSplits(amountNum, participants, sh, currency);
      }
      if (splitMode === "exact") {
        const ex = participants.map((m) => parseFloat(exacts[m.userId] ?? "0") || 0);
        return computeExactSplits(amountNum, participants, ex, currency);
      }
    } catch {
      return [];
    }
    return [];
  }, [amountNum, participants, currency, splitMode, percentages, shares, exacts]);

  const splitsValid = splits.length > 0 && splitsBalance(splits, amountNum);
  const splitLabel = useMemo(() => {
    if (splitMode === "equal") {
      return `Equally · ${participants.length} way${participants.length === 1 ? "" : "s"}`;
    }
    return {
      percent: "By percentage",
      shares: "By shares",
      exact: "Exact amounts",
    }[splitMode];
  }, [splitMode, participants.length]);

  // Resolve the effective payer once we know members and the current user.
  // On edit this falls back to the stored payer, never the editor.
  const effectivePayer = useMemo(() => {
    if (!user?.uid) return null;
    return resolveExpensePayer({
      selectedPayerUid: payerUid,
      existing,
      currentUser: {
        userId: user.uid,
        userName: displayNameOf(user),
      },
      members,
    });
  }, [payerUid, existing, members, user]);
  const payerIsMe = effectivePayer?.userId === user?.uid;

  // Optimistic add/update: the row appears (or updates) in the trip's
  // expenses[] cache immediately, then the Firestore write resolves.
  // On error, cache rolls back and the sheet shows the error toast.
  const mutation = useTripMutation<ExpenseEntry, void, Trip>({
    keys: [queryKeys.trip(id ?? "")],
    mutationFn: async (entry) => {
      if (!trip) throw new Error("Not ready");
      const { error } = isEdit
        ? await updateExpenseInTrip(trip.id, entry)
        : await addExpenseToTrip(trip.id, entry);
      if (error) throw error;
    },
    applyOptimistic: (_keys, entry) => [
      (prev) => {
        if (!prev) return prev;
        if (isEdit) {
          return {
            ...prev,
            expenses: (prev.expenses ?? []).map((e) => (e.id === entry.id ? entry : e)),
          };
        }
        return { ...prev, expenses: [entry, ...(prev.expenses ?? [])] };
      },
    ],
  });

  // Delete mutation — only reachable in edit mode.
  const deleter = useTripMutation<string, void, Trip>({
    keys: [queryKeys.trip(id ?? "")],
    mutationFn: async (expenseId) => {
      if (!trip) throw new Error("Not ready");
      await unwrap(removeExpenseFromTrip(trip.id, expenseId));
    },
    applyOptimistic: (_keys, expenseId) => [
      (prev) =>
        prev
          ? { ...prev, expenses: (prev.expenses ?? []).filter((e) => e.id !== expenseId) }
          : prev,
    ],
    successToast: "Expense removed.",
  });

  const buildEntry = (): ExpenseEntry | null => {
    if (!trip || !user?.uid) return null;
    if (fxUnavailable) {
      setError(`No exchange rate for ${activeCurrency} → ${currency}. Enter the amount in ${currency}.`);
      return null;
    }
    if (!Number.isFinite(amountNum) || amountNum <= 0) {
      setError("Enter an amount above zero.");
      return null;
    }
    if (members.length === 0) {
      setError("Add at least one member to the trip before logging expenses.");
      return null;
    }
    const now = Timestamp.now();
    const fallbackName = CATEGORIES.find((c) => c.key === type)?.label ?? "Expense";
    const expenseTs = expenseYmd
      ? Timestamp.fromDate(parseYmdLocalNoon(expenseYmd))
      : now;
    const creatorName = displayNameOf(user);
    const payer = effectivePayer ?? { userId: user.uid, userName: creatorName };
    return {
      id: existing?.id ?? uuid(),
      name: name.trim() || fallbackName,
      amount: amountNum,
      currency,
      expenseDate: expenseTs,
      type,
      // Preserve original creator on edit — only who *logged* it. The
      // payer is edited separately via payerUid/payerName below.
      creatorId: existing?.creatorId ?? user.uid,
      creatorName: existing?.creatorName ?? creatorName,
      ...payerFieldsFor(payer, existing?.creatorId ?? user.uid),
      splitMode: splitMode !== "equal" ? splitMode : undefined,
      splits,
      originalAmount: activeCurrency !== currency ? rawAmount : undefined,
      originalCurrency: activeCurrency !== currency ? activeCurrency : undefined,
      fxRate:
        activeCurrency !== currency && fxRateValue != null ? fxRateValue : undefined,
      createdAt: existing?.createdAt ?? now,
      updatedAt: now,
    };
  };

  const submit = () => {
    const entry = buildEntry();
    if (!entry) return;
    mutation.mutate(entry, {
      onSuccess: () => {
        toast.success(isEdit ? "Expense updated." : "Expense saved.", {
          description: `${formatMoney(entry.amount, currency)} · split ${entry.splits.length} way${entry.splits.length === 1 ? "" : "s"}`,
        });
        if (!isEdit) {
          logAnalyticsEvent("expense_added", {
            trip_id: trip?.id ?? "",
            currency,
            split_count: entry.splits.length,
          });
        }
        close();
      },
      onError: (err) => {
        setError(err instanceof Error ? err.message : "Couldn't save the expense.");
      },
    });
  };

  const confirmDelete = async () => {
    if (!existing) return;
    await deleter.mutateAsync(existing.id);
    setConfirmDeleteOpen(false);
    close();
  };

  // Read-only bounce — must live below all hook calls above.
  if (readOnly && id) {
    return <Navigate to={`/trip/${id}/expenses`} replace />;
  }

  return (
    <>
    <Sheet open guardUnsaved onOpenChange={(open) => !open && !mutation.isPending && close()}>
      <SheetContent>
        <SheetHeader title={isEdit ? "Edit expense" : "Add expense"} />

        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
          className="flex min-h-0 flex-1 flex-col"
        >
          <SheetBody className="flex flex-col gap-4">
            {isLoading || !trip ? (
              <Skeleton className="h-40 w-full rounded-md" />
            ) : (
              <>
                {/* Amount — the visual anchor. Currency chip on the
                    right lets the user pick a merchant charge currency
                    different from the trip currency; the FX caption
                    then shows the converted amount + rate. */}
                <div className="border-b border-line pb-4">
                  <div className="flex items-baseline gap-2">
                    <span className="tabular text-xl font-semibold text-ink-muted">
                      {currencySymbol(activeCurrency)}
                    </span>
                    <Input
                      type="text"
                      inputMode="decimal"
                      placeholder="0.00"
                      value={amount}
                      onChange={(e) => {
                        setAmount(e.target.value.replace(/[^\d.,]/g, ""));
                        if (error) setError(null);
                      }}
                      className="!h-auto border-0 !p-0 !text-4xl !font-bold !tracking-tight tabular focus:!shadow-none"
                      autoFocus
                      invalid={!!error && amountNum <= 0}
                    />
                    <label className="inline-flex items-center rounded-full bg-bg-sunken px-2.5 py-1.5 text-[12px] font-semibold text-ink focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-primary">
                      <select
                        aria-label="Charge currency"
                        value={activeCurrency}
                        onChange={(e) => {
                          const v = e.target.value;
                          setChargeCurrency(v === currency ? null : v);
                        }}
                        className="bg-transparent text-ink outline-none"
                      >
                        {(() => {
                          // Trip currency at the top; then the rest.
                          const rest = SUPPORTED_CURRENCIES.filter(
                            (c) => c !== currency,
                          );
                          return [currency, ...rest].map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ));
                        })()}
                      </select>
                    </label>
                  </div>
                  {activeCurrency !== currency && rawAmount > 0 ? (
                    fxUnavailable ? (
                      <p className="mt-2 text-[12px] text-danger m-0">
                        No exchange rate for {activeCurrency} → {currency}. Enter the amount in{" "}
                        {currency}.
                      </p>
                    ) : (
                      <p className="mt-2 text-[12px] text-ink-subtle m-0">
                        ≈ {formatMoney(convertedAmount, currency)}
                        {fxRateValue != null ? ` · rate ${fxRateValue}` : ""}
                      </p>
                    )
                  ) : null}
                </div>

                {/* Category chips — active ring glides via layoutId. A
                    fade mask on the right hints there's more off-screen. */}
                <div>
                  <p className="mb-2 text-[13px] font-medium text-ink-muted">Category</p>
                  <LayoutGroup id="add-expense-category">
                    <div
                      className="no-scrollbar -mx-5 flex gap-2 overflow-x-auto px-5"
                      style={{
                        maskImage:
                          "linear-gradient(to right, black 0, black calc(100% - 32px), transparent 100%)",
                        WebkitMaskImage:
                          "linear-gradient(to right, black 0, black calc(100% - 32px), transparent 100%)",
                      }}
                    >
                      {CATEGORIES.map((c) => {
                        const on = type === c.key;
                        return (
                          <button
                            key={c.key}
                            type="button"
                            onClick={() => setType(c.key)}
                            className={cn(
                              "relative inline-flex shrink-0 items-center gap-1.5 rounded-full px-3 py-2 text-[13px] font-medium",
                              on ? "text-primary-ink" : "text-ink",
                            )}
                            aria-pressed={on}
                          >
                            {on ? (
                              <motion.span
                                layoutId="category-selected"
                                aria-hidden
                                className="absolute inset-0 rounded-full border border-primary bg-primary-soft"
                                transition={{ type: "spring", stiffness: 500, damping: 40 }}
                              />
                            ) : (
                              <span
                                aria-hidden
                                className="absolute inset-0 rounded-full bg-bg-sunken"
                              />
                            )}
                            <span className="relative inline-flex items-center gap-1.5">
                              <span
                                className="h-2 w-2 rounded-full"
                                style={{ background: c.color }}
                              />
                              {c.label}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </LayoutGroup>
                </div>

                <Field id="ex-name" label="What was it? (optional)">
                  <Input
                    id="ex-name"
                    placeholder="Bar Cañete"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </Field>

                {/* Paid by — tap to expand the member list inline */}
                <div className="border-t border-line pt-4">
                  <button
                    type="button"
                    className="flex w-full items-center justify-between"
                    aria-expanded={payerPickerOpen}
                    aria-label="Change who paid"
                    onClick={() => setPayerPickerOpen((v) => !v)}
                  >
                    <span className="text-[13px] font-medium text-ink-muted">Paid by</span>
                    <span className="inline-flex items-center gap-1.5 text-[15px] font-semibold text-ink">
                      {payerIsMe ? "You" : effectivePayer?.userName ?? "You"}
                      <ChevronDown
                        className={cn(
                          "h-4 w-4 text-ink-subtle transition-transform",
                          payerPickerOpen && "rotate-180",
                        )}
                      />
                    </span>
                  </button>
                  <AnimatePresence initial={false}>
                    {payerPickerOpen ? (
                      <motion.ul
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                        role="radiogroup"
                        aria-label="Payer"
                        className="overflow-hidden"
                      >
                        {members.map((m, i) => {
                          const selected = effectivePayer?.userId === m.userId;
                          return (
                            <li key={m.userId}>
                              <button
                                type="button"
                                role="radio"
                                aria-checked={selected}
                                onClick={() => {
                                  setPayerUid(m.userId);
                                  setPayerPickerOpen(false);
                                }}
                                className={cn(
                                  "mt-2 flex w-full items-center gap-3 rounded-md px-2 py-2 text-left transition-colors",
                                  selected ? "bg-primary-soft" : "hover:bg-bg-sunken",
                                )}
                              >
                                <Avatar name={m.userName} colorSeed={i} size="sm" />
                                <span className="flex-1 text-[14px] text-ink">
                                  {m.userName}
                                  {m.userId === user?.uid ? (
                                    <span className="ml-1 text-[12px] text-ink-subtle">· you</span>
                                  ) : null}
                                </span>
                                {selected ? (
                                  <Check className="h-4 w-4 text-primary" strokeWidth={2.5} />
                                ) : null}
                              </button>
                            </li>
                          );
                        })}
                      </motion.ul>
                    ) : null}
                  </AnimatePresence>
                </div>

                {/* When — trip-bounds-clamped date picker */}
                <div className="flex items-center justify-between border-t border-line pt-4">
                  <label
                    htmlFor="ex-date"
                    className="text-[13px] font-medium text-ink-muted"
                  >
                    When
                  </label>
                  <Input
                    id="ex-date"
                    type="date"
                    value={expenseYmd}
                    min={dateMin}
                    max={dateMax}
                    onChange={(e) => setExpenseYmd(e.target.value)}
                    className="max-w-[180px] !text-[15px] !font-semibold !text-ink"
                  />
                </div>

                {/* Split — mode tabs are always visible; per-member
                    inputs render below for non-equal modes. */}
                <div className="border-t border-line pt-4">
                  <div className="flex items-center justify-between">
                    <span className="text-[13px] font-medium text-ink-muted">Split</span>
                    <span className="text-[15px] font-semibold text-ink">{splitLabel}</span>
                  </div>
                  <div className="mt-3">
                    <Segmented<SplitMode>
                      value={splitMode}
                      onChange={setSplitMode}
                      ariaLabel="Split mode"
                      size="sm"
                      options={[
                        { value: "equal", label: "Equal" },
                        { value: "percent", label: "Percent" },
                        { value: "shares", label: "Shares" },
                        { value: "exact", label: "Exact" },
                      ]}
                    />
                  </div>
                  {splitMode !== "equal" ? (
                    <SplitInputs
                      mode={splitMode}
                      members={participants}
                      percentages={percentages}
                      shares={shares}
                      exacts={exacts}
                      onPercent={setPercentages}
                      onShares={setShares}
                      onExact={setExacts}
                      currency={currency}
                    />
                  ) : null}
                  {!splitsValid && amountNum > 0 && splitMode !== "equal" ? (
                    <p className="mt-2 text-[12px] text-danger m-0">
                      {splitMode === "percent"
                        ? "Percentages must sum to 100."
                        : splitMode === "exact"
                          ? `Exact amounts must sum to ${formatMoney(amountNum, currency)}.`
                          : "Add at least one positive share."}
                    </p>
                  ) : null}
                </div>

                {/* Live split preview — each row is a motion.li with a
                    stable layoutId so amounts tween as the total or the
                    split mode changes. */}
                {splits.length > 0 ? (
                  <motion.ul
                    layout
                    className="mt-1 flex flex-col divide-y divide-line border-t border-line"
                  >
                    <AnimatePresence initial={false}>
                      {splits.map((s, i) => (
                        <motion.li
                          key={s.userId}
                          layout
                          initial={{ opacity: 0, y: 4 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -4 }}
                          transition={{ duration: 0.18, ease: "easeOut" }}
                          className="flex items-center justify-between py-2.5"
                        >
                          <span className="inline-flex items-center gap-2.5 text-[14px] text-ink">
                            <Avatar name={s.userName} colorSeed={i} size="sm" />
                            {s.userName}
                            {s.userId === user?.uid ? (
                              <span className="text-[12px] text-ink-subtle">· you</span>
                            ) : null}
                          </span>
                          <motion.span
                            key={`amt-${s.userId}-${s.amount}`}
                            initial={{ opacity: 0.5 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.2 }}
                            className="tabular text-[14px] font-semibold text-ink"
                          >
                            {formatMoney(s.amount, currency)}
                          </motion.span>
                        </motion.li>
                      ))}
                    </AnimatePresence>
                  </motion.ul>
                ) : null}
              </>
            )}
          </SheetBody>
          <SheetFooter>
            <Button
              type="submit"
              block
              disabled={mutation.isPending || amountNum <= 0 || !trip || !splitsValid}
            >
              {mutation.isPending ? "Saving…" : isEdit ? "Save changes" : "Save expense"}
            </Button>
            {isEdit ? (
              <button
                type="button"
                onClick={() => setConfirmDeleteOpen(true)}
                disabled={mutation.isPending || deleter.isPending}
                className="inline-flex items-center justify-center gap-1.5 rounded-md py-2 text-[13px] font-semibold text-danger hover:bg-danger-soft/40 disabled:opacity-50"
              >
                <Trash2 className="h-4 w-4" />
                Delete expense
              </button>
            ) : null}
            <Button
              type="button"
              variant="ghost"
              block
              onClick={close}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>

    {isEdit && existing ? (
      <ConfirmDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        title={`Delete "${existing.name}"?`}
        description="It's removed for everyone. Balances recalculate."
        confirmLabel="Delete"
        destructive
        onConfirm={confirmDelete}
      />
    ) : null}
    </>
  );
}

// ─── Helpers ─────────────────────────────────────────────────
/**
 * Per-member inputs for percent / shares / exact modes. Renders one row
 * per trip member with a small avatar and a number field. Values are
 * strings so partial input ("33.") doesn't force a re-render every
 * character; parsing happens up in the parent's memo.
 */
function SplitInputs({
  mode,
  members,
  percentages,
  shares,
  exacts,
  onPercent,
  onShares,
  onExact,
  currency,
}: {
  mode: Exclude<SplitMode, "equal">;
  members: { userId: string; userName: string }[];
  percentages: Record<string, string>;
  shares: Record<string, string>;
  exacts: Record<string, string>;
  onPercent: (r: Record<string, string>) => void;
  onShares: (r: Record<string, string>) => void;
  onExact: (r: Record<string, string>) => void;
  currency: string;
}) {
  const suffix =
    mode === "percent" ? "%" : mode === "shares" ? "×" : currencySymbol(currency).trim();

  const values = mode === "percent" ? percentages : mode === "shares" ? shares : exacts;
  const setValue = (uid: string, next: string) => {
    const clean = next.replace(/[^\d.,]/g, "").replace(",", ".");
    const nextMap = { ...values, [uid]: clean };
    if (mode === "percent") onPercent(nextMap);
    else if (mode === "shares") onShares(nextMap);
    else onExact(nextMap);
  };

  return (
    <ul className="mt-3 flex flex-col divide-y divide-line border-t border-line">
      {members.map((m, i) => (
        <li key={m.userId} className="flex items-center justify-between gap-3 py-2.5">
          <span className="inline-flex items-center gap-2.5 text-[14px] text-ink">
            <Avatar name={m.userName} colorSeed={i} size="sm" />
            {m.userName}
          </span>
          <label className="inline-flex items-center gap-1.5 text-[13px] text-ink-subtle">
            <input
              type="text"
              inputMode="decimal"
              value={values[m.userId] ?? ""}
              onChange={(e) => setValue(m.userId, e.target.value)}
              placeholder="0"
              className="w-20 rounded-md border border-line-strong bg-bg-elevated px-2 py-1 text-right tabular text-[14px] font-semibold text-ink outline-none focus:border-primary"
              aria-label={`${m.userName} ${mode}`}
            />
            <span>{suffix}</span>
          </label>
        </li>
      ))}
    </ul>
  );
}
